import React from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { WishlistButton } from "./WishlistButton";
import { SizeStrip } from "./SizeStrip";

const FOOTWEAR_DEFAULTS = ["7", "8", "9", "10", "11"];

const _stripSizes = (product) => {
    const cat = (product.category || "").toLowerCase();
    if (!(cat.includes("foot") || cat.includes("shoe") || cat.includes("sneak"))) {
        return null;  // default XS-XL letters handled in SizeStrip
    }
    const sizes = product.sizes && product.sizes.length ? product.sizes : FOOTWEAR_DEFAULTS;
    if (sizes.length <= 5) return sizes;
    // Window of 5 centered on the recommended size so it's always visible.
    const recIdx = product.recommended_size != null
        ? sizes.findIndex((s) => String(s) === String(product.recommended_size))
        : -1;
    const center = recIdx >= 0 ? recIdx : Math.floor(sizes.length / 2);
    let start = Math.max(0, center - 2);
    if (start + 5 > sizes.length) start = sizes.length - 5;
    return sizes.slice(start, start + 5);
};

export const ProductCard = ({ product }) => {
    return (
        <div className="relative" data-testid={`product-card-wrap-${product.id}`}>
            <Link
                to={`/product/${product.id}`}
                data-testid={`product-card-${product.id}`}
                className="block bg-white border-2 border-black shadow-brutal card-lift relative"
            >
                {product.tag && (
                    <span
                        data-testid={`product-tag-${product.id}`}
                        className="absolute top-3 right-3 z-10 border-2 border-black bg-[#FF00FF] text-white px-2 py-1 text-[10px] font-bold uppercase tracking-widest"
                    >
                        {product.tag}
                    </span>
                )}
                {product.store_name && (
                    <span
                        data-testid={`product-store-${product.id}`}
                        className="absolute top-3 left-3 z-10 border-2 border-black bg-black text-[#CCFF00] px-2 py-1 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1"
                    >
                        <ExternalLink className="w-3 h-3" /> {product.store_name}
                    </span>
                )}
                <div className="aspect-[4/5] overflow-hidden border-b-2 border-black bg-[#FDFBF7]">
                    {product.image_url && (
                        <img
                            src={product.image_url}
                            alt={product.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                    )}
                </div>
                <div className="p-4 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                        {product.brand && (
                            <p className="font-mono text-[10px] uppercase tracking-widest text-black/50 truncate">
                                {product.brand}
                            </p>
                        )}
                        <p className="font-display font-extrabold uppercase tracking-tight truncate">
                            {product.name}
                        </p>
                        <p className="font-mono text-xs uppercase text-black/60">
                            {product.category}
                        </p>
                    </div>
                    <p className="font-display font-black text-xl">
                        ${Number(product.price).toFixed(0)}
                    </p>
                </div>
                {/* 5-dot size strip — shown only when matches engine attached recommended_size or matched_sizes */}
                {(product.recommended_size || (product.matched_sizes && product.matched_sizes.length)) && (
                    <div className="px-4 pb-3 -mt-1">
                        <SizeStrip
                            recommended={product.recommended_size}
                            matched={product.matched_sizes}
                            sizes={_stripSizes(product)}
                            testid={`product-sizestrip-${product.id}`}
                        />
                    </div>
                )}
            </Link>
            {/* Heart toggle — kept outside the <Link> so the click doesn't navigate */}
            <div className="absolute bottom-3 right-3 z-10">
                <WishlistButton productId={product.id} size="sm" testid={`product-wishlist-${product.id}`} />
            </div>
        </div>
    );
};
